export type WorkflowGroup = {
  id: number;
  name: string;
  slug: string;
  company: {
    id: number | null;
    name: string | null;
    slug: string | null;
  };
};

export type WorkflowRequestType = 'pto' | 'admin' | 'academic' | 'specific_shift' | 'cannot_work_specific_shift' | 'swap_shift' | 'time_off' | 'availability';

export type WorkflowShiftType = {
  key: string;
  label: string;
};

export type WorkflowSwapRecipientStatus = 'pending' | 'accepted' | 'declined' | 'closed';

export type WorkflowSwapRecipient = {
  userId: number;
  name: string | null;
  email: string | null;
  status: WorkflowSwapRecipientStatus;
  respondedAt: string | null;
  note: string | null;
  isCurrentUser: boolean;
};

export type WorkflowSwapContext = {
  offeredShiftEventId: string | null;
  offeredShift: {
    id: string | null;
    title: string | null;
    start: string | null;
    end: string | null;
    allDay: boolean;
    location: string | null;
  } | null;
  targetDates: string[];
  targetNote: string | null;
  recipientCount: number;
  recipients: WorkflowSwapRecipient[];
  acceptedBy: {
    userId: number | null;
    name: string | null;
  } | null;
  acceptedAt: string | null;
  viewerRole: 'requester' | 'recipient' | 'other';
  canCurrentUserRespond: boolean;
};

export type WorkflowRequestRecord = {
  id: string;
  title: string;
  details: string | null;
  start: string;
  end: string;
  allDay: boolean;
  timezone: string | null;
  requestType: WorkflowRequestType;
  visibility: 'private' | 'scheduling_admins';
  selectedDates: string[];
  shiftTypes: string[];
  workflowStatus: 'private' | 'pending' | 'approved' | 'denied' | 'cancelled';
  calendarStatus: string;
  submittedAt: string | null;
  version: number;
  queueState: 'private' | 'pending' | 'approved' | 'denied' | 'cancelled' | 'auto_approved' | 'modified' | 'withdrawn';
  lifecycle: Array<{ type: string; status: string; version?: number; occurred_at: string; actor_name?: string | null; note?: string | null }>;
  requester: {
    id: number | null;
    name: string | null;
    email: string | null;
  };
  group: WorkflowGroup;
  swapContext: WorkflowSwapContext | null;
  decision: {
    status: 'approved' | 'denied' | null;
    note: string | null;
    decidedAt: string | null;
    decidedByName: string | null;
  } | null;
};

export type WorkflowSummary = {
  private: number;
  pending: number;
  approved: number;
  denied: number;
  cancelled: number;
  auto_approved: number;
  modified: number;
  withdrawn: number;
};

type RawWorkflowGroup = {
  id: number;
  name: string;
  slug: string;
  company: {
    id: number | null;
    name: string | null;
    slug: string | null;
  };
};

type RawWorkflowRequestRecord = {
  id: string;
  title: string;
  details: string | null;
  start: string;
  end: string;
  all_day: boolean;
  timezone: string | null;
  request_type: WorkflowRequestType;
  visibility: 'private' | 'scheduling_admins';
  selected_dates?: string[];
  shift_types?: string[];
  workflow_status: 'private' | 'pending' | 'approved' | 'denied' | 'cancelled';
  calendar_status: string;
  submitted_at: string | null;
  version?: number;
  queue_state?: WorkflowRequestRecord['queueState'];
  lifecycle?: WorkflowRequestRecord['lifecycle'];
  requester: {
    id: number | null;
    name: string | null;
    email: string | null;
  };
  group: RawWorkflowGroup;
  swap_context?: {
    offered_shift_event_id?: string | number | null;
    offered_shift?: {
      id?: string | number | null;
      title?: string | null;
      start?: string | null;
      end?: string | null;
      all_day?: boolean;
      location?: string | null;
    } | null;
    target_dates?: string[];
    target_note?: string | null;
    recipients?: Array<{
      user_id?: number | null;
      name?: string | null;
      email?: string | null;
      status?: WorkflowSwapRecipientStatus;
      responded_at?: string | null;
      note?: string | null;
      is_current_user?: boolean;
    }>;
    recipient_count?: number;
    accepted_by?: {
      user_id?: number | null;
      name?: string | null;
    } | null;
    accepted_at?: string | null;
    viewer_role?: 'requester' | 'recipient' | 'other';
    can_current_user_respond?: boolean;
  } | null;
  decision: {
    status: 'approved' | 'denied' | null;
    note: string | null;
    decided_at: string | null;
    decided_by_name: string | null;
  } | null;
};

type RawWorkflowResponse = {
  available_groups: RawWorkflowGroup[];
  request_shift_types?: WorkflowShiftType[];
  summary: WorkflowSummary;
  requests: RawWorkflowRequestRecord[];
};

type RawWorkflowQueueResponse = RawWorkflowResponse & {
  selected_group: RawWorkflowGroup | null;
};

type RawSwapWorkspaceResponse = {
  available_groups: RawWorkflowGroup[];
  incoming_requests: RawWorkflowRequestRecord[];
  peer_options: Array<{
    id: number;
    name: string | null;
    email: string | null;
  }>;
};

const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

const mapGroup = (group: RawWorkflowGroup): WorkflowGroup => ({
  id: group.id,
  name: group.name,
  slug: group.slug,
  company: group.company,
});

const mapRequest = (record: RawWorkflowRequestRecord): WorkflowRequestRecord => ({
  id: record.id,
  title: record.title,
  details: record.details,
  start: record.start,
  end: record.end,
  allDay: record.all_day,
  timezone: record.timezone,
  requestType: record.request_type,
  visibility: record.visibility,
  selectedDates: record.selected_dates ?? [],
  shiftTypes: record.shift_types ?? [],
  workflowStatus: record.workflow_status,
  calendarStatus: record.calendar_status,
  submittedAt: record.submitted_at,
  version: record.version ?? 1,
  queueState: record.queue_state ?? record.workflow_status,
  lifecycle: record.lifecycle ?? [],
  requester: record.requester,
  group: mapGroup(record.group),
  swapContext: record.swap_context
    ? {
        offeredShiftEventId: record.swap_context.offered_shift_event_id != null
          ? String(record.swap_context.offered_shift_event_id)
          : null,
        offeredShift: record.swap_context.offered_shift
          ? {
              id: record.swap_context.offered_shift.id != null ? String(record.swap_context.offered_shift.id) : null,
              title: record.swap_context.offered_shift.title ?? null,
              start: record.swap_context.offered_shift.start ?? null,
              end: record.swap_context.offered_shift.end ?? null,
              allDay: Boolean(record.swap_context.offered_shift.all_day),
              location: record.swap_context.offered_shift.location ?? null,
            }
          : null,
        targetDates: record.swap_context.target_dates ?? [],
        targetNote: record.swap_context.target_note ?? null,
        recipientCount: record.swap_context.recipient_count ?? record.swap_context.recipients?.length ?? 0,
        recipients: (record.swap_context.recipients ?? []).map((recipient) => ({
          userId: Number(recipient.user_id ?? 0),
          name: recipient.name ?? null,
          email: recipient.email ?? null,
          status: recipient.status ?? 'pending',
          respondedAt: recipient.responded_at ?? null,
          note: recipient.note ?? null,
          isCurrentUser: Boolean(recipient.is_current_user),
        })),
        acceptedBy: record.swap_context.accepted_by
          ? {
              userId: record.swap_context.accepted_by.user_id ?? null,
              name: record.swap_context.accepted_by.name ?? null,
            }
          : null,
        acceptedAt: record.swap_context.accepted_at ?? null,
        viewerRole: record.swap_context.viewer_role ?? 'other',
        canCurrentUserRespond: Boolean(record.swap_context.can_current_user_respond),
      }
    : null,
  decision: record.decision
    ? {
        status: record.decision.status,
        note: record.decision.note,
        decidedAt: record.decision.decided_at,
        decidedByName: record.decision.decided_by_name,
      }
    : null,
});

const readJson = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    throw new Error(
      response.status === 403
        ? 'You do not have access to the requested scheduling scope.'
        : response.status === 401
          ? 'Your CARES session expired. Sign in again before continuing.'
          : response.status === 404
            ? 'This request is no longer available in the selected department.'
            : response.status === 409
              ? 'This request changed after the queue loaded. Refresh the queue before trying again.'
        : response.status === 422
          ? 'Please correct the request form and try again.'
          : `Request workflow call failed (${response.status}).`
    );
  }

  return response.json() as Promise<T>;
};

export const fetchMyScheduleRequests = async (): Promise<{
  availableGroups: WorkflowGroup[];
  requestShiftTypes: WorkflowShiftType[];
  summary: WorkflowSummary;
  requests: WorkflowRequestRecord[];
}> => {
  const payload = await readJson<RawWorkflowResponse>(
    await fetch('/api/v1/secure/my-schedule/requests', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );

  return {
    availableGroups: payload.available_groups.map(mapGroup),
    requestShiftTypes: payload.request_shift_types ?? [],
    summary: payload.summary,
    requests: payload.requests.map(mapRequest),
  };
};

export type MyScheduleRequestPayload = {
  group_id: number;
  title?: string;
  details?: string;
  start: string;
  end: string;
  allDay: boolean;
  visibility: 'private' | 'scheduling_admins';
  request_type: WorkflowRequestType;
  selected_dates?: string[];
  shift_types?: string[];
  swap_context?: {
    offered_shift_event_id: string | number;
    recipient_user_ids: number[];
    target_dates?: string[];
    target_note?: string | null;
  } | null;
};

export const createMyScheduleRequest = async (payload: MyScheduleRequestPayload): Promise<WorkflowRequestRecord> => {
  const response = await readJson<{ request: RawWorkflowRequestRecord }>(
    await fetch('/api/v1/secure/my-schedule/requests', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify(payload),
    })
  );

  return mapRequest(response.request);
};

export const updateMyScheduleRequest = async (requestId: string, payload: MyScheduleRequestPayload): Promise<WorkflowRequestRecord> => {
  const response = await readJson<{ request: RawWorkflowRequestRecord }>(
    await fetch(`/api/v1/secure/my-schedule/requests/${requestId}`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify(payload),
    })
  );

  return mapRequest(response.request);
};

export const deleteMyScheduleRequest = async (requestId: string): Promise<WorkflowRequestRecord> => {
  const response = await readJson<{ request: RawWorkflowRequestRecord }>(
    await fetch(`/api/v1/secure/my-schedule/requests/${requestId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
    })
  );

  return mapRequest(response.request);
};

export const fetchSchedulerRequestQueue = async (group?: string): Promise<{
  selectedGroup: WorkflowGroup | null;
  availableGroups: WorkflowGroup[];
  summary: WorkflowSummary;
  requests: WorkflowRequestRecord[];
}> => {
  const params = new URLSearchParams();
  if (group) {
    params.set('group', group);
  }

  const suffix = params.toString() ? `?${params.toString()}` : '';
  const payload = await readJson<RawWorkflowQueueResponse>(
    await fetch(`/api/v1/secure/scheduler-review/request-queue${suffix}`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );

  return {
    selectedGroup: payload.selected_group ? mapGroup(payload.selected_group) : null,
    availableGroups: payload.available_groups.map(mapGroup),
    summary: payload.summary,
    requests: payload.requests.map(mapRequest),
  };
};

export const fetchSwapShiftWorkspace = async (groupId?: number | string): Promise<{
  availableGroups: WorkflowGroup[];
  incomingRequests: WorkflowRequestRecord[];
  peerOptions: Array<{
    id: number;
    name: string | null;
    email: string | null;
  }>;
}> => {
  const params = new URLSearchParams();
  if (groupId != null && String(groupId) !== '') {
    params.set('group_id', String(groupId));
  }

  const suffix = params.toString() ? `?${params.toString()}` : '';
  const payload = await readJson<RawSwapWorkspaceResponse>(
    await fetch(`/api/v1/secure/my-schedule/swap-shifts/workspace${suffix}`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );

  return {
    availableGroups: payload.available_groups.map(mapGroup),
    incomingRequests: payload.incoming_requests.map(mapRequest),
    peerOptions: payload.peer_options,
  };
};

export const respondToSwapShiftRequest = async (
  requestId: string,
  payload: { decision: 'accepted' | 'declined'; note?: string | null }
): Promise<WorkflowRequestRecord> => {
  const response = await readJson<{ request: RawWorkflowRequestRecord }>(
    await fetch(`/api/v1/secure/my-schedule/swap-shifts/${requestId}/response`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify(payload),
    })
  );

  return mapRequest(response.request);
};

export const decideWorkflowRequest = async (groupId: number, requestId: string, payload: {
  decision: 'approved' | 'denied';
  note?: string;
  expected_version: number;
}): Promise<WorkflowRequestRecord> => {
  const response = await readJson<{ request: RawWorkflowRequestRecord }>(
    await fetch(`/api/v1/secure/groups/${groupId}/requests/${requestId}/approval`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify(payload),
    })
  );

  return mapRequest(response.request);
};

export const reopenWorkflowRequest = async (groupId: number, requestId: string, payload: { note: string; expected_version: number }): Promise<WorkflowRequestRecord> => {
  const response = await readJson<{ request: RawWorkflowRequestRecord }>(
    await fetch(`/api/v1/secure/groups/${groupId}/requests/${requestId}/reopen`, {
      method: 'POST', credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken },
      body: JSON.stringify(payload),
    })
  );
  return mapRequest(response.request);
};
